import type { PostRecord, HashtagRecord } from "@/types/api";
import { SITE } from "@/lib/seo/site";

/**
 * The fields a card actually renders. Everything else the API returns is
 * dropped before the record crosses into a client component.
 *
 * The API returns 37 fields per record and React serializes whatever object it
 * is handed, not the narrower type a component declares — so
 * `StickerCardDesktopExtras` declaring a 7-field local type did nothing to stop
 * the whole record landing in the RSC payload. At ~1.9KB per record that was
 * 23-27% of a listing page's payload, and the bulk of it was fields no card
 * reads: sticker_pack_zip, publisher_email, publisher_website,
 * privacy_policy_website, license_agreement_website, ios_appstore_link,
 * android_playstore_link, video_path, video_thumb_path, template_Id,
 * post_type_Id, status, is_like, is_follow, is_creator, trending, post_view,
 * is_user_verified — several of them long absolute URLs repeated on every card.
 * (`user_Id` is carried: the card links its author line to /u/<id>.)
 *
 * Server-side consumers (itemListSchema, listingFacts, metadata) keep the full
 * record: they run before the boundary and are unaffected.
 */
const CARD_FIELDS = [
  "post_Id",
  "post_title",
  "post_slug",
  "identifier",
  "post_description",
  "post_tags",
  "sticker_pack_wall",
  "user_Id",
  "user_name",
  "profile_photo",
  "is_animated",
  "created_on",
  "post_like",
  "post_downloads",
  "post_comments",
  "post_stickers",
  "post_size",
] as const satisfies ReadonlyArray<keyof PostRecord>;

export type CardRecord = Pick<PostRecord, (typeof CARD_FIELDS)[number]> &
  Partial<Pick<HashtagRecord, "stickers">>;

/**
 * Narrows a record to {@link CARD_FIELDS} before it crosses a client boundary.
 *
 * `stickers` is carried through when present: StickerCard branches on it to
 * decide between a pack cover and a thumbnail strip, so dropping it would
 * silently change how every hashtag card renders.
 *
 * Idempotent, and accepts an already-narrowed record: it copies only the keys
 * that are present, so running it twice is a no-op rather than a data loss.
 */
export function toCardRecord(record: CardRecord | PostRecord | HashtagRecord): CardRecord {
  const out = {} as Record<string, unknown>;
  for (const key of CARD_FIELDS) {
    if (key in record) out[key] = (record as unknown as Record<string, unknown>)[key];
  }
  const stickers = (record as HashtagRecord).stickers;
  if (Array.isArray(stickers)) out.stickers = stickers;
  return out as CardRecord;
}

/**
 * The API sometimes returns `sticker_pack_wall` as a blank string or a bare
 * "/" rather than omitting it. Both are truthy, and stripping the leading
 * slashes left an empty path, so the old code emitted `https://cdn.sticko.app/`
 * — the CDN root — as an image URL. Three of those were live in the JSON-LD of
 * every listing page (`primaryImageOfPage` on WebPage and CollectionPage, plus
 * the first `ItemList` item), pointing structured data at a non-image.
 */
function coverPath(record: Pick<PostRecord, "sticker_pack_wall">): string | undefined {
  const raw = record.sticker_pack_wall?.trim();
  if (!raw) return undefined;
  if (raw.startsWith("http")) return raw;
  const relative = raw.replace(/^\/+/, "").trim();
  return relative || undefined;
}

export function packCoverUrl(record: Pick<PostRecord, "sticker_pack_wall">): string {
  const cover = coverPath(record);
  if (!cover) return `${SITE.cdn}/static-content/sticko-logo.svg`;
  return cover.startsWith("http") ? cover : `${SITE.cdn}/${cover}`;
}

// A representative cover for a listing page's primaryImageOfPage. Returns the
// first record that has a real cover — never packCoverUrl's logo fallback,
// which Google's structured-data guidelines say not to use as a page image.
export function firstRealCover(
  records: Pick<PostRecord, "sticker_pack_wall">[],
): string | undefined {
  const withCover = records.find((r) => coverPath(r));
  return withCover ? packCoverUrl(withCover) : undefined;
}

/** True when the record has a usable cover image (not the logo fallback). */
export function hasRealCover(record: Pick<PostRecord, "sticker_pack_wall">): boolean {
  return coverPath(record) !== undefined;
}

/**
 * Build the canonical URL for a sticker-pack detail page.
 *
 * The Sticko API only resolves `/posts/web/slug/{post_slug}`, where post_slug
 * is the SEO-friendly form (e.g. "anime-wi94maszez"). The short `identifier`
 * (e.g. "wI94MASZEz") does NOT resolve. We fall back to identifier only when
 * post_slug is missing — the request will 404, but it's better than crashing.
 */
export function postPathFor(
  record: Pick<PostRecord, "identifier"> & { post_slug?: string | null },
): string {
  const slug = record.post_slug || record.identifier;
  return `/sticker-pack/${slug}`;
}

export function extractTags(record: Pick<PostRecord, "post_description" | "post_tags">): string[] {
  const description = record.post_description ?? "";
  if (description.includes("#")) {
    return description
      .split("#")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  if (record.post_tags) {
    return record.post_tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return [];
}

export function firstHashtag(
  record: Pick<PostRecord, "post_description" | "post_tags">,
): string | null {
  const tags = extractTags(record);
  return tags[0] ?? null;
}

export function formatCount(value: number | string | null | undefined): string {
  const num = typeof value === "string" ? Number(value) : (value ?? 0);
  if (!Number.isFinite(num) || num <= 0) return "0";
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return String(num);
}

export function formatSizeMb(bytes: number | string | null | undefined): string {
  const num = typeof bytes === "string" ? Number(bytes) : (bytes ?? 0);
  if (!Number.isFinite(num) || num <= 0) return "0";
  return (num / 1000 / 1024).toFixed(2);
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const yr = Math.floor(mo / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

export function stickerThumbUrl(name: string | null | undefined): string {
  if (!name) return "";
  if (name.startsWith("http")) return name;
  return `${SITE.cdn}/${name.replace(/^\/+/, "")}`;
}
